import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { EspaceClub } from "../components/EspaceClub";

// -------- Espace : demande de lien magique --------
export default function Espace() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/client/magic-link", { email });
      setSent(true);
    } catch { toast.error("Erreur, veuillez réessayer"); }
    finally { setLoading(false); }
  }

  return (
    <div data-testid="espace-page" className="max-w-2xl mx-auto px-6 md:px-12 py-24">
      <p className="font-hand text-xl text-[#A3876A] mb-2">Ton espace privé</p>
      <h1 className="font-serif text-5xl text-[#2C2621] mb-6">Retrouve ton Club et tout ce que je t'ai envoyé</h1>
      <p className="text-[#4A4036] leading-relaxed mb-10">
        Saisis l'email avec lequel tu as rejoint le Club de Lettres, acheté un programme ou une correspondance.
        Je t'envoie un lien de connexion sécurisé — pas de mot de passe à retenir.
      </p>
      {sent ? (
        <div data-testid="espace-sent" className="bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
          <p className="font-serif text-2xl mb-3">Un instant…</p>
          <p className="text-[#4A4036]">Si un compte existe pour <strong>{email}</strong>, tu recevras un email d'ici quelques minutes avec ton lien de connexion. Il est valable 30 minutes.</p>
          <p className="text-xs text-[#7C7064] mt-4">Pas d'email ? Vérifie tes indésirables ou <button onClick={()=>setSent(false)} className="underline">réessaie</button>.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
          <label className="text-sm text-[#7C7064] block mb-2">Ton email</label>
          <input data-testid="espace-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)}
            className="field-input mb-6" placeholder="toi@exemple.fr"/>
          <button data-testid="espace-submit" disabled={loading} className="btn-primary">
            {loading ? "Envoi…" : "Recevoir mon lien"}
          </button>
        </form>
      )}
    </div>
  );
}

// -------- Dashboard : après clic sur le lien magique --------
export function EspaceDashboard() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.get(`/client/me?token=${encodeURIComponent(token)}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Lien expiré ou invalide"));
  useEffect(() => {
    if (!token) { setErr("Lien invalide"); return; }
    load();
  }, [token]); // eslint-disable-line

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <h1 className="font-serif text-4xl mb-4">Oups</h1>
      <p className="text-[#4A4036] mb-8">{err}</p>
      <Link to="/espace" className="btn-primary">Demander un nouveau lien</Link>
    </div>
  );
  if (!data) return <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#7C7064]">Chargement…</div>;

  const progressPercent = data.programme ? Math.round((data.programme.last_sent_week / 52) * 100) : 0;

  return (
    <div data-testid="espace-dashboard" className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <p className="font-hand text-xl text-[#A3876A]">Bienvenue</p>
      <h1 className="font-serif text-5xl text-[#2C2621] mb-2">Ton espace</h1>
      <p className="text-[#7C7064] mb-12">{data.email}</p>

      <EspaceClub token={token} subs={data.mailclub_all} onRefresh={load}/>

      {/* Programme */}
      {data.programme && (
        <section data-testid="espace-programme" className="mb-14 bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
          <p className="font-hand text-xl text-[#A3876A]">Ton programme</p>
          <h2 className="font-serif text-3xl mb-4">
            {data.programme.programme_type === "epistolaire" ? "Programme Épistolaire" : "Programme Essentiel"}
          </h2>
          <div className="flex items-center gap-6">
            <div className="flex-1">
              <div className="h-2 bg-[#E8DCC8] rounded-full overflow-hidden">
                <div className="h-full bg-[#A3876A]" style={{width: `${progressPercent}%`}}/>
              </div>
              <p className="text-sm text-[#4A4036] mt-2">Semaine <strong>{data.programme.last_sent_week}</strong> sur 52 · {progressPercent}%</p>
            </div>
            <span className={`text-xs px-3 py-1 ${data.programme.status === "active" ? "bg-[#7B8A72] text-white" : "bg-[#DCCBBA]"}`}>
              {data.programme.status === "active" ? "En cours" : "Terminé"}
            </span>
          </div>
        </section>
      )}

      {/* Correspondances */}
      {data.correspondances.length > 0 && (
        <section data-testid="espace-correspondances" className="mb-14">
          <h2 className="font-serif text-3xl mb-6">Tes correspondances</h2>
          <div className="space-y-3">
            {data.correspondances.map(c => (
              <div key={c.correspondance_id} className="bg-[#FDFBF7] border border-[#E8DCC8] p-5 flex justify-between items-center">
                <div>
                  <p className="font-serif">{c.product_type === "coffret" ? "Réponse + coffret" : "Réponse simple"}</p>
                  <p className="text-xs text-[#7C7064]">Commandée le {new Date(c.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-[#F5F0E6]">{
                  c.status === "paid" ? "Attente de ta lettre" :
                  c.status === "letter_received" ? "Lettre reçue" :
                  c.status === "to_write" ? "En cours d'écriture" :
                  c.status === "sent" ? "Réponse envoyée" :
                  c.status === "closed" ? "Terminée" : c.status
                }</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Codes valables */}
      {data.codes.filter(c => !c.used_at).length > 0 && (
        <section data-testid="espace-codes" className="mb-14">
          <h2 className="font-serif text-3xl mb-6">Tes codes correspondance</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {data.codes.filter(c => !c.used_at).map(c => (
              <div key={c.code} className="bg-[#F5F0E6] border border-[#E8DCC8] p-5">
                <p className="font-mono text-lg tracking-widest text-[#2C2621]">{c.code}</p>
                <p className="text-xs text-[#7C7064] mt-1">Expire le {new Date(c.expires_at).toLocaleDateString("fr-FR")}</p>
                <Link to="/correspondance" className="text-sm underline text-[#A3876A] mt-2 inline-block">Utiliser ce code →</Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Abonnement */}
      {data.subscription && (
        <section data-testid="espace-subscription" className="mb-14 bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
          <h2 className="font-serif text-3xl mb-3">Ton abonnement</h2>
          <p className="text-[#4A4036]">Statut : <strong>{data.subscription.status}</strong></p>
          <p className="text-sm text-[#7C7064]">Mois versés : {data.subscription.paid_invoices_count || 0}</p>
          <Link to="/mon-abonnement" className="btn-outline mt-4 inline-block">Gérer</Link>
        </section>
      )}

      {/* Commandes */}
      {data.orders.length > 0 && (
        <section className="mb-14">
          <h2 className="font-serif text-3xl mb-6">Historique de commandes</h2>
          <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0E6] text-left"><tr>
                <th className="p-3">Date</th><th className="p-3">Produit</th><th className="p-3">Montant</th>
              </tr></thead>
              <tbody>
                {data.orders.map(o => (
                  <tr key={o.order_id} className="border-t border-[#E8DCC8]">
                    <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3">{o.lookup_key}</td>
                    <td className="p-3">{((o.amount || 0) / 100).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Factures */}
      {data.invoices && data.invoices.length > 0 && (
        <section data-testid="espace-invoices" className="mb-14">
          <h2 className="font-serif text-3xl mb-6">Mes factures</h2>
          <div className="bg-[#FDFBF7] border border-[#E8DCC8] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0E6] text-left"><tr>
                <th className="p-3">Date</th>
                <th className="p-3">N° facture</th>
                <th className="p-3">Produit</th>
                <th className="p-3">Montant</th>
                <th className="p-3 text-right">PDF</th>
              </tr></thead>
              <tbody>
                {data.invoices.map(inv => (
                  <tr key={inv.invoice_id} className="border-t border-[#E8DCC8]" data-testid={`invoice-row-${inv.invoice_number}`}>
                    <td className="p-3 text-xs">{new Date(inv.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="p-3">{inv.product_name}</td>
                    <td className="p-3">{((inv.amount || 0) / 100).toFixed(2)} €</td>
                    <td className="p-3 text-right">
                      <a
                        href={inv.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#A3876A] hover:underline"
                        data-testid={`invoice-download-${inv.invoice_number}`}
                      >Télécharger →</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="text-center text-sm text-[#7C7064] mt-16">
        Une question ? <Link to="/contact" className="underline">Écris-moi</Link>.
      </p>
    </div>
  );
}
